import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen, PuzzlePiece, ArrowsClockwise, X,
  ArrowsLeftRight, TextAa, ChatCircleDots, Keyboard,
  Shuffle, ListChecks, MagnifyingGlass, SpeakerHigh,
  ArrowLeft, CalendarBlank, ArrowRight,
} from "@phosphor-icons/react";
import { Mascot }      from "@/components/styleguide/Mascot";
import { BloomBadge }  from "@/components/styleguide/ui/BloomBadge";
import { ChildAvatar } from "@/experiences/select-child/ChildAvatar";
import { api }         from "./api";
import { useAuth }     from "./AuthContext";
import TrialBanner     from "./TrialBanner";
import TrialEndedModal from "./TrialEndedModal";

const ACTIVITIES = [
  { icon: ListChecks,    label: "Meaning",    skill: "meaning",  ready: false },
  { icon: ArrowsLeftRight, label: "Synonyms", skill: "synonym",  ready: true  },
  { icon: TextAa,        label: "Antonyms",   skill: "antonym",  ready: true  },
  { icon: ChatCircleDots, label: "Sentences", skill: "sentence", ready: true  },
  { icon: Keyboard,      label: "Spelling",   skill: "spelling", ready: false },
  { icon: Shuffle,       label: "Mixed Quiz", skill: "mixed",    ready: true  },
];

const DIFF_V = { easy: "success", medium: "warning", hard: "primary" };

/* ── Word card sheet ──────────────────────────────────────────────────── */
function WordCardSheet({ word, onClose }) {
  function speak(t) {
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(t);
      u.lang = "en-GB"; u.rate = 0.85;
      window.speechSynthesis.speak(u);
    } catch {}
  }
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <button aria-label="Close" onClick={onClose}
        className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
      <motion.div
        data-testid="word-card-sheet"
        initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
        className="relative max-h-[90svh] w-full max-w-lg overflow-y-auto rounded-t-[2rem] bg-card shadow-soft-lg sm:rounded-[2rem]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-5 py-3 backdrop-blur">
          <button onClick={onClose}
            className="flex items-center gap-1.5 text-sm font-bold text-muted-foreground hover:text-foreground">
            <ArrowLeft size={14} weight="bold" /> Back to search
          </button>
          <button onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-border">
            <X size={15} weight="bold" />
          </button>
        </div>
        <div className="space-y-4 px-5 pb-8 pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-3xl font-black capitalize text-foreground sm:text-4xl">{word.word}</h2>
            <button onClick={() => speak(word.word)}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow active:scale-90">
              <SpeakerHigh size={20} weight="fill" />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {word.word_type  && <BloomBadge variant="neutral" className="px-2.5 py-1 text-xs">{word.word_type}</BloomBadge>}
            {word.difficulty && <BloomBadge variant={DIFF_V[word.difficulty] || "neutral"} className="px-2.5 py-1 text-xs">{word.difficulty}</BloomBadge>}
          </div>
          {word.meaning && <p className="text-base font-semibold leading-relaxed text-foreground/90">{word.meaning}</p>}
          {word.synonyms?.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Synonyms</p>
              <p className="mb-2 text-[10px] font-semibold text-muted-foreground/70">Words with a similar meaning</p>
              <div className="flex flex-wrap gap-2">
                {word.synonyms.map(s => <span key={s} className="rounded-full bg-teal/15 px-3 py-1.5 text-sm font-extrabold text-teal-ink">{s}</span>)}
              </div>
            </div>
          )}
          {word.antonyms?.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Antonyms</p>
              <p className="mb-2 text-[10px] font-semibold text-muted-foreground/70">Words with the opposite meaning</p>
              <div className="flex flex-wrap gap-2">
                {word.antonyms.map(a => <span key={a} className="rounded-full bg-destructive/10 px-3 py-1.5 text-sm font-extrabold text-destructive">{a}</span>)}
              </div>
            </div>
          )}
          {word.image_url && (
            <div className="overflow-hidden rounded-2xl border border-border/40 shadow-soft">
              {word.image_label && (
                <div className="bg-teal px-3 py-1.5 text-center">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-white">{word.image_label}</span>
                </div>
              )}
              <img src={word.image_url} alt={word.word} className="w-full object-contain" style={{ aspectRatio: "3/4" }} />
            </div>
          )}
          {word.sentences?.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">In a sentence</p>
              <div className="rounded-2xl bg-muted/60 px-4 py-3">
                <p className="text-sm font-semibold leading-relaxed text-foreground/85">{word.sentences[0]}</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Lookup sheet ─────────────────────────────────────────────────────── */
function LookupSheet({ childId, onClose }) {
  const [query,     setQuery]     = useState("");
  const [results,   setResults]   = useState(null);
  const [searching, setSearching] = useState(false);
  const [selected,  setSelected]  = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (query.trim().length < 3) { setResults(null); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const data = await api(`/search?q=${encodeURIComponent(query)}&child_id=${childId}`);
        setResults(data);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [query, childId]);

  if (selected) return (
    <AnimatePresence>
      <WordCardSheet word={selected} onClose={() => setSelected(null)} />
    </AnimatePresence>
  );

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <button aria-label="Close" onClick={onClose}
        className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
      <motion.div data-testid="lookup-sheet"
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
        className="relative w-full max-w-md rounded-t-[2rem] bg-card p-6 shadow-soft-lg sm:rounded-[2rem]"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-black text-foreground">Look up a word</h2>
          <button onClick={onClose} aria-label="Close"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-muted transition hover:bg-border">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="relative">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            autoFocus
            className="w-full rounded-2xl border border-border bg-background py-3 pl-9 pr-4 text-sm font-semibold text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            placeholder="Type a word to look up…"
            value={query} onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="mt-3 min-h-[80px]">
          {searching && <p className="py-4 text-center text-sm font-semibold text-muted-foreground">Searching…</p>}
          {results?.length === 0 && !searching && query.length >= 3 && (
            <p className="py-4 text-center text-sm font-semibold text-muted-foreground">No results for "{query}"</p>
          )}
          {results?.map(w => (
            <button key={w.word_id} onClick={() => setSelected(w)}
              className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-muted/50">
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-extrabold capitalize text-foreground">{w.word}</p>
                {w.meaning && <p className="mt-0.5 truncate text-xs font-semibold text-muted-foreground">{w.meaning}</p>}
              </div>
              {w.match_type === "synonym" && w.match_word && (
                <span className="shrink-0 rounded-full bg-teal/15 px-2 py-0.5 text-[10px] font-bold text-teal-ink">
                  via {w.match_word}
                </span>
              )}
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── No-plans prompt (shown when no active plans) ────────────────────── */
function NoPlansPrompt() {
  return (
    <>
      <style>{`
        @keyframes no-plan-border { 0%,100% { opacity:.35 } 50% { opacity:.85 } }
      `}</style>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-violet-500/10 via-purple-500/7 to-fuchsia-400/10 p-5"
      >
        {/* Animated dashed border — opacity breathes, no colour weirdness */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[1.75rem] border-2 border-dashed border-violet-400"
          style={{ animation: "no-plan-border 2.8s ease-in-out infinite" }}
        />

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100/70">
              <CalendarBlank size={18} weight="fill" className="text-violet-500" />
            </span>
            <div>
              <p className="font-display text-sm font-black text-foreground">No learning plan yet</p>
              <p className="text-xs font-semibold text-muted-foreground">
                Ask a grown-up to set one from the dashboard
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-violet-100/60 px-2.5 py-1 text-[11px] font-extrabold text-violet-500">
            Not set up
          </span>
        </div>
      </motion.div>
    </>
  );
}

/* ── Plans banner (shown when active plans exist) ─────────────────────── */
function PlansBanner({ plans, childId }) {
  const navigate = useNavigate();
  if (!plans?.length) return null;

  const totalWords   = plans.reduce((s, p) => s + (p.word_ids?.length ?? 0), 0);
  const totalQuizzes = plans.reduce((s, p) => s + (p.stats?.quiz_count ?? 0), 0);

  return (
    <>
      <style>{`
        @keyframes plan-shimmer {
          0%   { transform: translateX(-180%) skewX(-12deg); }
          100% { transform: translateX(320%)  skewX(-12deg); }
        }
      `}</style>
      <motion.button
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -4, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 340, damping: 26 }}
        onClick={() => navigate(`/vocab/child/${childId}/plans`)}
        className="relative w-full overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-400 p-5 shadow-lg text-left active:scale-[0.98]"
      >
        {/* Slow shimmer sweep */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-white/15 to-transparent"
          style={{ animation: "plan-shimmer 5s ease-in-out 1s infinite" }}
        />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
            <CalendarBlank size={18} weight="fill" className="text-white" />
          </span>
          <div>
            <p className="font-display text-sm font-black text-white">My Plans</p>
            <p className="text-[11px] font-semibold text-white/75">
              {plans.length} plan{plans.length !== 1 ? "s" : ""} · {totalWords} words
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1 text-xs font-extrabold text-white/90">
          Open <ArrowRight size={12} weight="bold" />
        </span>
      </div>

      {totalQuizzes > 0 && (
        <p className="mt-3 text-xs font-semibold text-white/75">
          {totalQuizzes} quiz{totalQuizzes !== 1 ? "zes" : ""} done across all plans
        </p>
      )}
    </motion.button>
    </>
  );
}

/* ── Child Home ───────────────────────────────────────────────────────── */
export default function ChildHome() {
  const { childId } = useParams();
  const navigate    = useNavigate();
  const { user }    = useAuth();

  const [child,          setChild]          = useState(null);
  const [playOpen,       setPlayOpen]       = useState(false);
  const [lookupOpen,     setLookupOpen]     = useState(false);
  const [plansData,      setPlansData]      = useState(null);
  const [revisionInfo,   setRevisionInfo]   = useState(null);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // Auto-show modal when trial has expired
  useEffect(() => {
    if (user?.subscription_status === "expired") {
      setShowTrialModal(true);
    }
  }, [user?.subscription_status]);

  useEffect(() => {
    api("/children").then(list => {
      const c = list.find(x => x.child_id === childId);
      if (!c) navigate("/vocab/children", { replace: true });
      else setChild(c);
    }).catch(() => navigate("/vocab", { replace: true }));

    api(`/children/${childId}/plans/active`).then(setPlansData).catch(() => setPlansData([]));
    api(`/revision/info?child_id=${childId}`).then(setRevisionInfo).catch(() => {});
  }, [childId, navigate]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div
      className="relative min-h-[100svh] overflow-hidden bg-gradient-to-b from-[#FFF7EF] via-[#FFF1E6] to-[#DCF3EC]"
      data-testid="child-home"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute right-[12%] top-[6%] h-20 w-20 rounded-full bg-secondary/40 blur-xl" />
        <div className="absolute left-[8%] top-[36%] h-28 w-28 rounded-full bg-teal/20 blur-2xl" />
      </div>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 sm:px-8">
        {child ? (
          <span className="flex items-center gap-2.5 rounded-2xl bg-card/90 p-1.5 pr-4 shadow-soft backdrop-blur">
            <ChildAvatar variant={child.avatar} className="h-10 w-10" />
            <span className="leading-tight">
              <span className="block font-display text-sm font-black text-foreground">{child.first_name}</span>
              <span className="block text-[10px] font-extrabold text-primary-strong">Year {child.school_year} Explorer</span>
            </span>
          </span>
        ) : <span />}
        <button
          data-testid="switch-child-btn"
          onClick={() => navigate("/vocab/children")}
          className="rounded-full bg-card/85 px-4 py-2.5 text-sm font-extrabold text-muted-foreground shadow-soft backdrop-blur transition hover:text-foreground"
        >
          Switch
        </button>
      </div>

      {/* Trial banner — shown when ≤3 days left in trial */}
      <div className="relative z-10">
        <TrialBanner onSubscribe={() => setShowTrialModal(true)} />
      </div>

      <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center px-6 pb-16 pt-4 text-center sm:pt-10">
        {/* Mascot */}
        <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}>
          <Mascot size={92} mood="happy" animated={false} />
        </motion.div>
        <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-foreground sm:text-4xl">
          {greeting}{child ? `, ${child.first_name}` : ""}!
        </h1>
        <p className="mt-1.5 text-sm font-semibold text-muted-foreground sm:text-base">
          Ready to grow some new words today?
        </p>

        {/* Plans slot — prompt when none, banner when some */}
        {plansData !== null && (
          <div className="mt-5 w-full">
            {plansData.length === 0
              ? <NoPlansPrompt />
              : <PlansBanner plans={plansData} childId={childId} />
            }
          </div>
        )}

        {/* Learn + Play */}
        <div className="mt-8 grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
          <motion.button
            data-testid="learn-tile"
            whileHover={{ y: -5 }} whileTap={{ scale: 0.97 }}
            onClick={() => navigate(`/vocab/child/${childId}/learn`)}
            className="rounded-[2rem] bg-primary p-7 text-left text-primary-foreground shadow-glow"
          >
            <BookOpen size={38} weight="fill" />
            <p className="mt-3 font-display text-2xl font-black">Learn</p>
            <p className="mt-1 text-sm font-bold opacity-90">Meet new words with meanings, hints and stories.</p>
          </motion.button>

          <motion.button
            data-testid="play-tile"
            whileHover={{ y: -5 }} whileTap={{ scale: 0.97 }}
            onClick={() => setPlayOpen(true)}
            className="rounded-[2rem] bg-secondary p-7 text-left text-secondary-foreground shadow-soft"
          >
            <PuzzlePiece size={38} weight="fill" />
            <p className="mt-3 font-display text-2xl font-black">Play</p>
            <p className="mt-1 text-sm font-bold opacity-80">Quizzes and games to practise your words.</p>
          </motion.button>
        </div>

        {/* Revision tile */}
        {revisionInfo?.words_seen >= 20 && (
          <motion.button
            data-testid="revision-tile"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4 }} whileTap={{ scale: 0.97 }}
            onClick={() => navigate(`/vocab/child/${childId}/revision${child?.quiz_help_enabled ? "?help=true" : ""}`)}
            className="mt-4 w-full rounded-[2rem] bg-teal p-6 text-left text-white shadow-soft"
          >
            <div className="flex items-center gap-3">
              <ArrowsClockwise size={32} weight="fill" className="opacity-90" />
              <div>
                <p className="font-display text-xl font-black">Revise</p>
                <p className="text-sm font-bold opacity-85">
                  Quiz your {revisionInfo.words_seen} learned words — tricky ones come up more!
                </p>
              </div>
            </div>
          </motion.button>
        )}

        {/* Look up a word */}
        <button
          data-testid="lookup-btn"
          onClick={() => setLookupOpen(true)}
          className="mt-5 flex items-center gap-2 rounded-full bg-card/80 px-5 py-2.5 text-sm font-extrabold text-muted-foreground shadow-soft backdrop-blur transition hover:bg-card hover:text-foreground active:scale-[0.97]"
        >
          <MagnifyingGlass size={15} weight="bold" />
          Look up a word
        </button>
      </div>

      {/* Play sheet */}
      <AnimatePresence>
        {playOpen && (
          <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button aria-label="Close" onClick={() => setPlayOpen(false)}
              className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
            <motion.div
              data-testid="play-sheet"
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 26 }}
              className="relative w-full max-w-md rounded-t-[2rem] bg-card p-6 shadow-soft-lg sm:rounded-[2rem]"
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-display text-xl font-black text-foreground">Play</h2>
                <button onClick={() => setPlayOpen(false)} aria-label="Close"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-muted transition hover:bg-border">
                  <X size={18} weight="bold" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {ACTIVITIES.map(({ icon: Icon, label, skill, ready }) =>
                  ready ? (
                    <button key={label} data-testid={`play-${skill}`}
                      onClick={() => { setPlayOpen(false); navigate(`/vocab/child/${childId}/quiz/${skill}${child?.quiz_help_enabled ? "?help=true" : ""}`); }}
                      className="flex min-h-[76px] flex-col justify-between rounded-2xl border-2 border-border bg-background p-3 text-left transition hover:border-primary/40 hover:bg-accent active:scale-[0.97]"
                    >
                      <span className="flex items-center gap-2 font-display text-sm font-extrabold text-foreground">
                        <Icon size={17} weight="bold" className="text-teal-ink" /> {label}
                      </span>
                      <BloomBadge variant="teal" className="w-fit px-2 py-0.5 text-[10px]">Play</BloomBadge>
                    </button>
                  ) : (
                    <div key={label} className="flex min-h-[76px] flex-col justify-between rounded-2xl border-2 border-dashed border-border bg-background p-3 opacity-70">
                      <span className="flex items-center gap-2 font-display text-sm font-extrabold text-foreground">
                        <Icon size={17} weight="bold" className="text-teal-ink" /> {label}
                      </span>
                      <BloomBadge variant="neutral" className="w-fit px-2 py-0.5 text-[10px]">Coming soon</BloomBadge>
                    </div>
                  )
                )}
              </div>
              <p className="mt-3 text-center text-xs font-bold text-muted-foreground">
                Pick a quiz type to practise your words!
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {lookupOpen && <LookupSheet childId={childId} onClose={() => setLookupOpen(false)} />}
      </AnimatePresence>

      {/* Trial ended modal — shown when Subscribe is clicked or trial expires */}
      <AnimatePresence>
        {showTrialModal && (
          <TrialEndedModal
            canDismiss={user?.subscription_status !== "expired"}
            onDismiss={() => setShowTrialModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}